import {
  type EpocusEncounter,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-encounter";
import {
  type EpocusExam,
  RXDB_EPOCUS_EXAM_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-exam";
import type { ExamType } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import {
  type EpocusReview,
  RXDB_EPOCUS_REVIEW_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-review";
import { useServerTime } from "$global-providers/server-time-provider/provider";
import { useCallback, useMemo } from "react";
import type { RxCollection } from "rxdb";
import { useRxData } from "rxdb-hooks";
import { getTurnaroundMinutes } from "../../_lib/review-turnaround";
import {
  DEFAULT_REVIEWER_WINDOW,
  isWithinReviewerWindow,
  type ReviewerWindowKey,
  reviewerWindowStart,
} from "../../_lib/reviewer-stats-window";

export type ReviewHistoryRow = {
  reviewId: string;
  examId: string;
  patientName: string | null;
  examType: ExamType | null;
  performerId: string | null;
  signedAt: string;
  turnaroundMinutes: number | null;
  agreement: EpocusReview["agreement"];
};

export const useReviewerReviewHistory = (
  reviewerId: string,
  windowKey: ReviewerWindowKey = DEFAULT_REVIEWER_WINDOW,
): ReviewHistoryRow[] => {
  const { getDisplayTime } = useServerTime();

  const reviewsQuery = useCallback(
    (collection: RxCollection<EpocusReview>) =>
      collection.find({ selector: { reviewerId: { $eq: reviewerId } } }),
    [reviewerId],
  );
  const { result: reviews } = useRxData<EpocusReview>(
    RXDB_EPOCUS_REVIEW_COLLECTION_NAME,
    reviewsQuery,
  );

  const examIds = useMemo(
    () => reviews.map((review) => review.examId),
    [reviews],
  );

  const examsQuery = useCallback(
    (collection: RxCollection<EpocusExam>) =>
      examIds.length > 0
        ? collection.find({ selector: { id: { $in: examIds } } })
        : collection.find({ selector: { id: { $eq: "__none__" } } }),
    [examIds],
  );
  const { result: exams } = useRxData<EpocusExam>(
    RXDB_EPOCUS_EXAM_COLLECTION_NAME,
    examsQuery,
  );

  const encounterIds = useMemo(
    () => exams.map((exam) => exam.encounterId),
    [exams],
  );

  const encountersQuery = useCallback(
    (collection: RxCollection<EpocusEncounter>) =>
      encounterIds.length > 0
        ? collection.find({ selector: { id: { $in: encounterIds } } })
        : collection.find({ selector: { id: { $eq: "__none__" } } }),
    [encounterIds],
  );
  const { result: encounters } = useRxData<EpocusEncounter>(
    RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
    encountersQuery,
  );

  return useMemo(() => {
    const windowStart = reviewerWindowStart(
      windowKey,
      getDisplayTime().getTime(),
    );

    const examById = new Map(exams.map((exam) => [exam.id, exam]));
    const encounterById = new Map(
      encounters.map((encounter) => [encounter.id, encounter]),
    );

    return reviews
      .flatMap((review): ReviewHistoryRow[] => {
        const { signedAt } = review;
        if (!signedAt || !isWithinReviewerWindow(signedAt, windowStart)) {
          return [];
        }

        const exam = examById.get(review.examId);
        const encounter = exam
          ? encounterById.get(exam.encounterId)
          : undefined;

        return [
          {
            reviewId: review.id,
            examId: review.examId,
            patientName: encounter?.patientName ?? null,
            examType: exam?.examType ?? null,
            performerId: encounter?.performerId ?? null,
            signedAt,
            turnaroundMinutes: encounter
              ? getTurnaroundMinutes(review, encounter)
              : null,
            agreement: review.agreement,
          },
        ];
      })
      .sort(
        (a, b) =>
          new Date(b.signedAt).getTime() - new Date(a.signedAt).getTime(),
      );
  }, [reviews, exams, encounters, windowKey, getDisplayTime]);
};
